import { useCallback, useEffect, useMemo, useState } from "react";
import { Tab, Nav, Button, Dropdown } from "react-bootstrap";
import { Event, EventAdmin, isSameDay } from "../../../models/interfaces/Event";
import { Competition, CompetitionChoice, isCompetition, sortCompetitions } from "../../../models/interfaces/Competition";
import { MatchStatus } from "../../../models/interfaces/Match";
import { EventStandingsView } from "./EventStandingsView";
import { EventLeadersView } from "./EventLeadersView";
import { CompetitionEntry } from "../../../models/interfaces/Competitions/CompetitionEntry";
import { EventMatchDisplayView } from "./EventMatchDisplayView";
import { SportProvider } from "../../../models/interfaces/SportProvider";
import { User } from "../../../models/interfaces/User";
import styled from "styled-components";
import { useResolverList } from "../../../helpers/useResolverList";
import { withTimeout } from '../../../helpers/loading';
import { LoadStatus } from "../../../components/containers/LoadStatus";

import { FeaturedEventShell, MatchPlaceholders } from './FeaturedEventPlaceholder';

import { useTimedLoad } from '../../../helpers/useTimedLoad';
import { CompetitionPickerDropDownView } from './CompetitionPickerViews';

interface EventProps {
    sportProvider: SportProvider;
    event: Event;
    user ?: User;
}

export function eventTabStorageKey(eventID: string) {
    return `featuredEventViewTab:${eventID}`
}

export function eventCompetitionStorageKey(eventID: string) {
    return `featuredEventViewSelectedCompetition:${eventID}`
}

const SmallScreenPicker = styled(Dropdown)`
    background-color: transparent;
    @media only screen and (min-width: 431px) {
        display: none;
    }
`

const LargeScreenPicker = styled.div`
    @media only screen and (max-width: 430px) {
        display: none;
    }
`


export function FeaturedEventView({ sportProvider, event, user }: EventProps) {
    const [selectedChoice, setSelectedChoice] = useState<CompetitionChoice | undefined>(undefined);
    const competitionLoad = useResolverList<CompetitionChoice>(event.competitionSummaryProvider || event.competitionProvider, 'division-names');
    const competitions = useMemo(() => [...competitionLoad.items].sort(sortCompetitions), [competitionLoad.items]);
    const loadSelected = useCallback((): Promise<Competition | undefined> => {
        if (!selectedChoice) return Promise.resolve(undefined);
        // Other event implementations may still supply full competitions.
        if (isCompetition(selectedChoice)) return Promise.resolve(selectedChoice);
        return sportProvider.competitionResolver(selectedChoice.id, event, true).asAPromise();
    }, [selectedChoice, sportProvider, event]);
    const selectedLoad = useTimedLoad(loadSelected, 'selected-competition', !!selectedChoice);
    const selectedCompetition = selectedLoad.data;
    const [selectedCompetitionEntry, setSelectedCompetitionEntry] = useState<CompetitionEntry | undefined>(undefined);

    const [selectedTab, setSelectedTab] = useState<string>(() => {
        const storedTab = localStorage.getItem(eventTabStorageKey(event.id));
        return storedTab && ["schedule", "results", "leaderboard", "standings"].includes(storedTab) ? storedTab : "schedule";
    });

    const showMatches = selectedTab === 'schedule' || selectedTab === 'results';
    const matchLoad = useResolverList(showMatches ? selectedCompetition?.matchProvider : undefined, 'matches');
    const teamLoad = useResolverList(showMatches ? selectedCompetition?.teamProvider : undefined, 'teams');
    useEffect(() => {
        if (!showMatches || matchLoad.error || teamLoad.error || (!matchLoad.loading && !teamLoad.loading)) {
            selectedCompetition?.finishInitialLoad?.();
        }
    }, [selectedCompetition, showMatches, matchLoad.loading, teamLoad.loading, matchLoad.error, teamLoad.error]);
    useEffect(() => () => selectedCompetition?.finishInitialLoad?.(), [selectedCompetition]);

    const matches = useMemo(() => ({
        scheduledMatches: matchLoad.items.filter(match => match.status !== MatchStatus.completed).sort((a, b) => a.date.getTime() - b.date.getTime()),
        completedMatches: matchLoad.items.filter(match => match.status === MatchStatus.completed).sort((a, b) => b.date.getTime() - a.date.getTime()),
    }), [matchLoad.items]);
    const sortedTeams = useMemo(() => [...teamLoad.items].sort((a, b) => a.name.localeCompare(b.name)), [teamLoad.items]);
    const teams = teamLoad.loading && sortedTeams.length === 0 ? undefined : sortedTeams;

    const handleSelectTab = (tab: string | null) => {
        setSelectedTab(tab ?? 'schedule');
    };

    useEffect(() => {
        localStorage.setItem(eventTabStorageKey(event.id), selectedTab);
    }, [event.id, selectedTab]);

    const selectedCompetitionStorageKey = eventCompetitionStorageKey(event.id);
    useEffect(() => {
        if (!selectedCompetition) {
            return
        }
        localStorage.setItem(selectedCompetitionStorageKey, selectedCompetition.id);
        if (selectedCompetitionEntry?.competition.id !== selectedCompetition?.id) {
            setSelectedCompetitionEntry(undefined);
        }
    }, [selectedCompetition, competitions, selectedCompetitionStorageKey, selectedCompetitionEntry?.competition.id])

    const selectedTeamStorageKey = `featuredEventViewSelectedTeam:${event.id}`
    useEffect(() => {
        // Preserve the current selection as more divisions arrive. Prefer the saved division
        // while loading. Choose the alphabetical default only once all names are known.
        if (selectedChoice && competitions.some(item => item.id === selectedChoice.id)) return;
        const preferredId = localStorage.getItem(selectedCompetitionStorageKey);
        const preferred = competitions.find(item => item.id === preferredId);
        if (preferred || (!competitionLoad.loading && !competitionLoad.error)) {
            setSelectedChoice(preferred || competitions[0]);
        }
    }, [competitions, competitionLoad.loading, competitionLoad.error, selectedChoice, selectedCompetitionStorageKey]);

    useEffect(() => {
        if (!showMatches || teamLoad.loading) return;
        const preferredId = localStorage.getItem(selectedTeamStorageKey);
        setSelectedCompetitionEntry(teamLoad.items.find(team => team.id === preferredId));
    }, [teamLoad.items, teamLoad.loading, selectedTeamStorageKey, showMatches]);

    var dateString: string | undefined = undefined;
    if (event.attributes.displayCompetitionDates) {
        const startDate = event.attributes.competitionDateRange.startDate;
        const endDate = event.attributes.competitionDateRange.endDate;
        const singleDayEvent = isSameDay(startDate, endDate);

        if (singleDayEvent) {
            dateString = startDate.toLocaleDateString();
        } else {
            dateString = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
        }
    }

    const [eventAdmin, setEventAdmin] = useState<EventAdmin | undefined>(undefined);

    useEffect(() => {
        let active = true;
        setEventAdmin(undefined);
        if (user) {
            withTimeout(event.adminForUser(user)).then(admin => {
                if (active) setEventAdmin(admin);
            }, () => {});
        }
        return () => { active = false; };
    }, [event, user]);

    return (
        <FeaturedEventShell>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px'}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                    <a href={`/events/${event.id}`}>
                        <h4 style={{marginBottom: '0px', fontWeight: 700, fontSize: '22px', lineHeight: '1.25'}}>{event.attributes.name}</h4>
                    </a>
                    { dateString && (<h6 style={{ color: 'var(--wpa-muted)', marginBottom: 0 }}>{dateString}</h6>) }
                </div>
                {
                    eventAdmin && (
                        <a href={`/events/${event.id}/admin`}>
                            <Button style={{ backgroundColor: "var(--wpa-yellow)", color: "var(--wpa-dark-blue)", border: "none", fontWeight: 700 }}>Manage</Button>
                        </a>
                    )
                }
            </div>
            <Tab.Container activeKey={selectedTab} onSelect={handleSelectTab} unmountOnExit>
                <Nav className="eventTabPicker">
                    <SmallScreenPicker>
                        <Dropdown.Toggle>
                            {selectedTab.toUpperCase()}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{backgroundColor: 'var(--wpa-surface)'}}>
                            <Nav.Item>
                                <Nav.Link eventKey="schedule">Schedule</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="results">Results</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="leaderboard">Leaderboard</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="standings">Standings</Nav.Link>
                            </Nav.Item>
                        </Dropdown.Menu>
                    </SmallScreenPicker>
                    <LargeScreenPicker>
                    <div style={{display: 'flex', flexDirection: 'row', gap: '5px'}}>
                            <Nav.Item>
                                <Nav.Link eventKey="schedule">Schedule</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="results">Results</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="leaderboard">Leaderboard</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="standings">Standings</Nav.Link>
                            </Nav.Item>
                        </div>
                    </LargeScreenPicker>
                </Nav>
                <Tab.Content style={{ width: '100%' }}>
                    <LoadStatus {...competitionLoad} label="divisions" quietLoading />
                    {!competitionLoad.loading && !competitionLoad.error && competitions.length === 0 && <p>No divisions available for this tournament.</p>}
                    {showMatches && selectedCompetition && <>
                        <LoadStatus {...matchLoad} label="matches" quietLoading />
                        <LoadStatus {...teamLoad} label="teams" quietLoading />
                    </>}
                    <LoadStatus {...selectedLoad} label="selected division" quietLoading />
                    {!selectedCompetition && competitions.length > 0 && <CompetitionPickerDropDownView
                        selectedCompetition={selectedChoice} competitions={competitions} onCompetitionSelect={setSelectedChoice} />}
                    {!selectedCompetition && (competitionLoad.loading || selectedLoad.loading) && <MatchPlaceholders filters />}
                    {selectedCompetition && (
                        <>
                            <Tab.Pane eventKey="schedule" transition={false}>
                                <EventMatchDisplayView
                                    key={`${selectedCompetition.id}:${selectedCompetitionEntry?.id || 'all'}`}
                                    sportProvider={sportProvider}
                                    filter={{
                                        competitionSelect: {
                                            competitions,
                                            selectedCompetition: selectedCompetition,
                                            onCompetitionSelect(competition) {
                                                setSelectedChoice(competition);
                                            },
                                        },
                                        teamSelect: {
                                            competitionEntries: teams,
                                            selectedCompetitionEntry,
                                            onSelectCompetitionEntry(entry) {
                                                setSelectedCompetitionEntry(entry);
                                                if (entry) localStorage.setItem(selectedTeamStorageKey, entry.id);
                                                else localStorage.removeItem(selectedTeamStorageKey);
                                            },
                                        }
                                    }}
                                    matches={matches.scheduledMatches}
                                    loading={matchLoad.loading || matchLoad.error}
                                    noContentText={'No scheduled matches for this division.'}
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey="results" transition={false}>
                                <EventMatchDisplayView
                                    key={`${selectedCompetition.id}:${selectedCompetitionEntry?.id || 'all'}`}
                                    sportProvider={sportProvider}
                                    filter={{
                                        competitionSelect: {
                                            competitions,
                                            selectedCompetition: selectedCompetition,
                                            onCompetitionSelect(competition) {
                                                setSelectedChoice(competition);
                                            },
                                        },
                                        teamSelect: {
                                            competitionEntries: teams,
                                            selectedCompetitionEntry,
                                            onSelectCompetitionEntry(entry) {
                                                setSelectedCompetitionEntry(entry);
                                                if (entry) localStorage.setItem(selectedTeamStorageKey, entry.id);
                                                else localStorage.removeItem(selectedTeamStorageKey);
                                            },
                                        }
                                    }}
                                    matches={matches.completedMatches}
                                    loading={matchLoad.loading || matchLoad.error}
                                    noContentText={'No completed matches for this division.'}
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey="leaderboard" transition={false}>
                                <EventLeadersView
                                    competitions={competitions}
                                    selectedCompetition={selectedCompetition}
                                    onSelectCompetition={setSelectedChoice}
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey="standings" transition={false}>
                                <EventStandingsView
                                    competitions={competitions}
                                    selectedCompetition={selectedCompetition}
                                    onSelectCompetition={setSelectedChoice}
                                />
                            </Tab.Pane>
                        </>
                    )}
                </Tab.Content>
            </Tab.Container>
        </FeaturedEventShell>
    );
}
